import { ISO_639_1_LOCALE, ISO_3166_1_LOCALE } from '@cineamo/ui/src/helpers/locale-helper';

import { ContentDto } from '../models/content/ContentDto.types';

import { getMovieBackdropImage, getMoviePosterImage, getMovieTitle, MovieVersionDto } from './movie-helper';

export type ContentImageFilterOptions = {
    language?: ISO_639_1_LOCALE;
    countryCode?: ISO_3166_1_LOCALE;
    useBackdropAsFallback?: boolean;
    usePosterAsFallback?: boolean;
    usePlaceholder?: boolean;
};

/**
 * Custom contents can carry an optional English description (`descriptionEn`) alongside
 * the default/German `description`. Picks `descriptionEn` when the site locale is English
 * and it was actually filled in, otherwise falls back to `description`.
 */
export function getLocalizedContentDescription(
    content?: Pick<ContentDto, 'description' | 'descriptionEn'>,
    locale?: string
): string {
    if (locale?.toLowerCase().startsWith('en') && content?.descriptionEn) {
        return content.descriptionEn;
    }
    return content?.description ?? '';
}

export function getContentName(
    content?: ContentDto,
    movie?: MovieVersionDto | null,
    locale?: string,
    countryCode?: ISO_3166_1_LOCALE
): string | undefined {
    let name = content?.name;
    if (movie) {
        name = getMovieTitle(movie, locale, countryCode);
    }
    return name;
}

export function getContentPosterImage(
    content?: ContentDto,
    movie?: MovieVersionDto | null,
    options?: ContentImageFilterOptions
): string | undefined {
    const { language, countryCode, useBackdropAsFallback = false, usePlaceholder = true } = options || {};

    let image = content?.posterImageUrl;
    if (movie) {
        image = getMoviePosterImage(movie, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder
        });
    }
    return image;
}

export function getContentBackdropImage(
    content?: ContentDto,
    movie?: MovieVersionDto | null,
    options?: ContentImageFilterOptions
): string | undefined {
    const { language, countryCode, usePosterAsFallback = false, usePlaceholder = true } = options || {};

    let image = content?.backdropImageUrl;
    if (movie) {
        image = getMovieBackdropImage(movie, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder
        });
    }
    return image;
}
